import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Phone, Mail, MessageCircle, Clock, User, Save, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Admin dialog to edit the public-site contact footer rendered at
 * flexlivingsf.com. Persisted under settings.type="site_contact" via
 * GET/PUT /api/public/[admin/]site-contact.
 */
export function SiteContactDialog({ open, onOpenChange, adminPass }) {
  const [form, setForm] = useState({ name: '', phone: '', whatsapp: '', email: '', best_times: '' });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    fetch(`${API}/api/public/site-contact`)
      .then(r => r.json())
      .then(d => { if (!cancelled) setForm({ name: d.name || '', phone: d.phone || '', whatsapp: d.whatsapp || '', email: d.email || '', best_times: d.best_times || '' }); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open]);

  const field = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/public/admin/site-contact`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: adminPass, contact: form }),
      });
      if (res.ok) { toast.success('Public contact updated'); onOpenChange(false); }
      else toast.error('Failed to save');
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="site-contact-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-lg flex items-center gap-2">
            <Phone className="h-5 w-5 text-primary" />Public Site Contact
          </DialogTitle>
          <DialogDescription>
            These details appear in the footer of the public customer site at <span className="font-mono">flexlivingsf.com</span>. Leave any field blank to hide it.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
        ) : (
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-muted-foreground mb-1 flex items-center gap-1.5"><User className="h-3.5 w-3.5" />Name</Label>
              <Input value={form.name} onChange={e => field('name', e.target.value)} placeholder="e.g., Ari" data-testid="site-contact-name" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground mb-1 flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" />Phone</Label>
              <Input value={form.phone} onChange={e => field('phone', e.target.value)} placeholder="e.g., 305-956-6201" data-testid="site-contact-phone" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground mb-1 flex items-center gap-1.5"><MessageCircle className="h-3.5 w-3.5" />WhatsApp</Label>
              <Input value={form.whatsapp} onChange={e => field('whatsapp', e.target.value)} placeholder="Same as phone works" data-testid="site-contact-whatsapp" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground mb-1 flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" />Email</Label>
              <Input value={form.email} onChange={e => field('email', e.target.value)} placeholder="e.g., rentals@flexlivingsf.com" data-testid="site-contact-email" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground mb-1 flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" />Best times to reach</Label>
              <Input value={form.best_times} onChange={e => field('best_times', e.target.value)} placeholder="e.g., Mon – Sun, 9am – 6pm" data-testid="site-contact-best-times" />
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving || loading} data-testid="site-contact-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}Save
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
